import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import d from 'dayjs';
import { Formik, Form, useFormikContext } from 'formik';
import cc from 'classcat';
import styles from './filterShips.module.sass';
import {
  useAuthStore,
  useShipSearchStore,
} from '../../../providers/RootStoreProvider';
import { DatePicker } from '../../DatePicker';
import { formatter } from '../../../utils/formatter';
import { DayCruise } from '../../DayCruise';
import { Tabs } from '../../Tabs';
import { Text, Anchor } from '../../Text';
import { ShowMore } from '../../SearchResults/components/ShowMore';
import { Icon } from '../../Icon';
import { DatePickerModal } from '../../DatePickerModal/DatePickerModal';
import { Badge } from '../../Badge';
import { declOfNum } from '../../../utils/declOfNum';
import { IconSVG } from '../../SpriteSVG';
import { SkeletonRoute } from '../../Skeleton/SkeletonRoute';
import { TooltipDiscount } from '../../TooltipDiscount';

const AutoSubmitFrom = observer(() => {
  const store = useShipSearchStore();

  const setFilters = filters => store.setFilters(filters);
  const clearFilters = () => store.clearFilters();
  const setParamsFilters = () => store.setParamsFilters();
  const { values, submitForm } = useFormikContext();

  useEffect(() => {
    clearFilters();
    setFilters(values);
    submitForm();
    setParamsFilters();
  }, [values]);

  return null;
});

const defaultSearchValues = {
  departure: [],
  direction: [],
  duration: [],
  dates: '',
};

export const FilterShips = observer(
  ({ isMobile, landing, sea, tabs, years, shipType, ownerId, url }) => {
    const store = useShipSearchStore();
    const authStore = useAuthStore();

    const router = useRouter();
    const { amount } = store;
    const { cruises } = store;

    const isFunSun = ownerId === 100025;
    const isAstoria = ownerId === 100022;

    store.shipUrl = url;

    const setPage = () => store.setPage();

    const getList = async () => await store.getList();
    const setParamsFilters = params => store.setParamsFilters(params);
    // const removeBlankProp = params => store.removeBlankProp(params);

    const { type, visit_cities, cruise_length, cruise_dates } = router.query;
    const toArray = str => str?.split(',').filter(Boolean) ?? [];

    return (
      <div>
        <Formik
          initialValues={{
            type: type ?? years[0],
            direction: toArray(visit_cities),
            duration: toArray(cruise_length),
            dates: cruise_dates?.split(',').map(date => new Date(date)) ?? '',
            days: store.days ?? {},
          }}
          onSubmit={async () => {
            setParamsFilters();
            await getList();
          }}
        >
          {({
            values,
            resetForm,
            isSubmitting,
            touched,
            errors,
            setValues,
          }) => (
            <>
              <div className="container container--start">
                <Tabs
                  name="type"
                  items={tabs}
                  isMobile={isMobile}
                  onChange={resetForm}
                  onReset={() => {
                    resetForm();
                    setValues({ ...values, ...defaultSearchValues });
                  }}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <div className={styles.cruises}>
                  <div className="container container--start">
                    <div className={styles.innerInfo}>
                      <div className={styles.cruisesHeader}>
                        <Text variant="heading-4" component={'h2'}>
                          {/* Нашли {amount} маршрутов на {shipType} */}
                          Расписание круизов и цены
                        </Text>
                      </div>
                      <Form
                        className={cc([
                          styles.search,
                          { [styles.landing]: landing },
                        ])}
                      >
                        <AutoSubmitFrom />
                        <div className={styles.one}>
                          {isMobile ? (
                            <DatePickerModal
                              id="dates"
                              label="Дата"
                              styleView="compact"
                              minDate={store.minDate}
                              maxDate={store.maxDate}
                              disabledDate={store.disabledDate}
                              value={values.dates}
                              error={touched.dates && errors.dates}
                            />
                          ) : (
                            <DatePicker
                              id="dates"
                              label="Дата"
                              styleView="compact"
                              minDate={store.minDate}
                              maxDate={store.maxDate}
                              disabledDate={store.disabledDate}
                              value={values.dates}
                              error={touched.dates && errors.dates}
                            />
                          )}
                        </div>
                        <DayCruise
                          id="duration"
                          label="Продолжительность"
                          view="compact"
                          rangeDays={store.days}
                          value={values.duration}
                          error={touched.duration && errors.duration}
                        />
                      </Form>
                      {/* <FilterShips url={url} compact values isMobile={isMobile} /> */}
                      {cruises.length !== 0 ? (
                        <div className={styles.cruisesAll}>
                          {cruises?.map((i, key) => (
                            <div key={key} className={styles.cruisesInfo}>
                              <div>
                                <div className={styles.cruisesInfoData}>
                                  <Text variant="heading-8">
                                    {d(i.kr_date_start).format('D MMMM')}{' '}
                                    {d(i.kr_date_start).format('YYYY') !==
                                      d(i.kr_date_end).format('YYYY') &&
                                      d(i.kr_date_start).format('YYYY')}{' '}
                                    — {d(i.kr_date_end).format('D MMMM')}{' '}
                                    {d(i.kr_date_end).format('YYYY')}
                                  </Text>
                                  <div className={styles.cruisesInfoDataRight}>
                                    {i.kr_days} дн./{i.kr_nights} нч.
                                  </div>
                                  {sea !== 'sea' &&
                                    i.free_places_left < 20 &&
                                    i.free_places_left !== -1 && (
                                      <div>
                                        {!isMobile && (
                                          <Badge
                                            view="freeplaces"
                                            size="smUltra"
                                            icon="fire"
                                          >
                                            осталось {i.free_places_left}{' '}
                                            {declOfNum(i.free_places_left, [
                                              'каюта',
                                              'каюты',
                                              'кают',
                                            ])}
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                  {sea !== 'sea' && i.tur_check !== 0 && (
                                    <div>
                                      {!isMobile && (
                                        <Badge
                                          view="tour"
                                          size="smUltra"
                                          icon="tour"
                                        >
                                          {i.tur_field || 'Круиз + тур'}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {sea !== 'sea' &&
                                  isMobile &&
                                  i.free_places_left < 20 &&
                                  i.free_places_left !== -1 && (
                                    <div className={styles.freeplaces}>
                                      {isMobile && (
                                        <Badge
                                          view="freeplaces"
                                          size="smUltra"
                                          icon="fire"
                                        >
                                          осталось {i.free_places_left}{' '}
                                          {declOfNum(i.free_places_left, [
                                            'каюта',
                                            'каюты',
                                            'кают',
                                          ])}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                {sea !== 'sea' &&
                                  i.tur_check !== 0 &&
                                  isMobile && (
                                    <div className={styles.freeplaces}>
                                      <Badge
                                        view="tour"
                                        size="smUltra"
                                        icon="tour"
                                      >
                                        {i.tur_field || 'Круиз + тур'}
                                      </Badge>
                                    </div>
                                  )}
                                <div className={styles.cruisesInfoRoute}>
                                  {i.route_full}
                                </div>
                                {i.tur_check !== 0 && (
                                  <TooltipDiscount
                                    trigger={
                                      <Text variant="reviewHref" topGap={16}>
                                        {`${i.tur_title} `}
                                        {i.tur_duration
                                          ? `(${i.tur_duration})`
                                          : ''}
                                      </Text>
                                    }
                                    content={<div>{i.tooltip_desc}</div>}
                                  />
                                )}
                                {/* <Text>
                                {i.route_arr.length > 10 ? (
                                <div>
                                  <Text>
                                    {i.kr_start_city} –
                                    <ModalRoadTrip
                                      title={i.t_title}
                                      dateStart={d(i.kr_date_start).format(
                                        'D MMMM',
                                      )}
                                      dateEnd={d(i.kr_date_end).format(
                                        'D MMMM',
                                      )}
                                      countDays={i.kr_days}
                                      countNights={i.kr_nights}
                                      startCity={i.kr_start_city}
                                      countCity={i.route_arr}
                                      endCity={i.route_end}
                                      type={'ship'}
                                      price={i.min_price_discounted}
                                    />
                                    – {i.route_end}
                                  </Text>
                                </div>
                                ) : (
                                  i.route_full
                                )}
                              </Text> */}
                              </div>

                              <div className={styles.cruisesInfoRight}>
                                {/* {i.min_price_discounted > 200 && isMobile && (
                                <div className={styles.cruisesInfoPrice}>
                                  {i.min_price_discounted > 200 &&
                                    i.min_price_discounted !== 0 &&
                                    i.min_price !== 0 && (
                                      <div
                                        className={styles.cruisesInfoPriceNew}
                                      >
                                        от {formatter(i.min_price_discounted)} ₽
                                      </div>
                                    )}
                                  {i.min_price !== i.min_price_discounted && (
                                    <div className={styles.cruisesInfoPriceOld}>
                                      от {formatter(i.min_price)} ₽
                                    </div>
                                  )}
                                </div>
                              )}
                              {i.min_price_discounted < 200 && isMobile && (
                                <div className={styles.cruisesInfoPriceNew}>
                                  цена по запросу
                                </div>
                              )} */}
                                <div className={styles.cruisesInfoRightButton}>
                                  <div className={styles.discountAll}>
                                    <div>
                                      {i.cashback === 1 && isMobile && (
                                        <div className={styles.cashbackAllMob}>
                                          <Icon
                                            width={24}
                                            height={24}
                                            rightGap={8}
                                            icon={
                                              <IconSVG
                                                name={'promo/cashback'}
                                                width={24}
                                                hanging={24}
                                              />
                                            }
                                            title="Иконка Гарантий"
                                          />
                                          <span>
                                            Кэшбэк 20% при оплате картой МИР
                                          </span>
                                        </div>
                                      )}
                                    </div>{' '}
                                    {sea !== 'sea' &&
                                      isMobile &&
                                      i.explicit_discounts?.length !== 0 &&
                                      i.explicit_discounts?.seazon_discount
                                        ?.value && (
                                        <div className={styles.increaseRbMob}>
                                          <Icon
                                            width={24}
                                            height={24}
                                            rightGap={8}
                                            icon={
                                              <IconSVG
                                                name={'promo/sale'}
                                                width={24}
                                                height={24}
                                              />
                                            }
                                            title="Иконка Рубля"
                                          />{' '}
                                          {
                                            <span>
                                              {i.explicit_discounts
                                                ?.seazon_discount?.value &&
                                                i.explicit_discounts
                                                  ?.seazon_discount?.value}
                                              % Скидка раннего бронирования
                                            </span>
                                          }
                                        </div>
                                      )}
                                    {sea !== 'sea' &&
                                      isMobile &&
                                      i.explicit_discounts?.length !== 0 &&
                                      i.explicit_discounts?.seazon_discount
                                        ?.length !== 0 &&
                                      i.explicit_discounts?.seazon_discount
                                        ?.days_before_rise && (
                                        <div className={styles.increaseMob}>
                                          <Icon
                                            width={24}
                                            height={24}
                                            rightGap={8}
                                            icon={
                                              <IconSVG
                                                name={'promo/ruble'}
                                                width={24}
                                                height={24}
                                              />
                                            }
                                            title="Иконка Рубля"
                                          />
                                          Цена вырастет через
                                          <span className={styles.increaseDays}>
                                            {' '}
                                            {
                                              i.explicit_discounts
                                                ?.seazon_discount
                                                ?.days_before_rise
                                            }{' '}
                                            {i.explicit_discounts
                                              ?.seazon_discount
                                              ?.days_before_rise &&
                                              declOfNum(
                                                i.explicit_discounts
                                                  ?.seazon_discount
                                                  ?.days_before_rise,
                                                ['день', 'дня', 'дней'],
                                              )}
                                          </span>
                                        </div>
                                      )}
                                    <div>
                                      {i.special_note && isMobile && (
                                        <div className={styles.bonusMob}>
                                          <Icon
                                            icon={
                                              <IconSVG
                                                name={'promo/note'}
                                                width={24}
                                                height={24}
                                              />
                                            }
                                            rightGap={8}
                                            title="Спец предложение"
                                            width={24}
                                            height={24}
                                          />
                                          <Text> {i.special_note}</Text>
                                          {/* <p>{i.special_note}</p> */}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      {i.special_offer !== 0 && isMobile && (
                                        <div className={styles.bonusMob}>
                                          <Icon
                                            icon={
                                              <IconSVG
                                                name={'promo/present'}
                                                width={24}
                                                height={24}
                                              />
                                            }
                                            rightGap={8}
                                            title="Спец предложение"
                                            width={24}
                                            height={24}
                                          />
                                          <Text>
                                            Спецпредложение -
                                            {i.special_offer_value}%
                                          </Text>
                                          {/* <p>{i.special_note}</p> */}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <Anchor
                                    variant="priceButtonShips"
                                    href={
                                      sea === 'sea'
                                        ? `/sea_cruise/${i.id}`
                                        : `/cruise/${i.id}`
                                    }
                                  >
                                    <span>
                                      {sea && i.min_price_discounted < 200
                                        ? 'цена по запросу'
                                        : ` Круиз от ${formatter(
                                            i.min_price_discounted,
                                          )} ₽ ${isFunSun ? '' : '/чел.'}`}
                                    </span>
                                  </Anchor>
                                  {i.cashback === 1 && !isMobile && (
                                    <div className={styles.cashbackAll}>
                                      <Icon
                                        width={24}
                                        height={24}
                                        rightGap={8}
                                        icon={
                                          <IconSVG
                                            name={'promo/cashback'}
                                            width={24}
                                            hanging={24}
                                          />
                                        }
                                        title="Иконка Гарантий"
                                      />
                                      <span>
                                        Кэшбэк 20% при оплате картой МИР
                                      </span>
                                    </div>
                                  )}
                                  {sea !== 'sea' &&
                                    !isMobile &&
                                    i.explicit_discounts?.length !== 0 &&
                                    i.explicit_discounts?.seazon_discount
                                      ?.value &&
                                    i.black_friday !== 1 && (
                                      <div className={styles.increaseRb}>
                                        <Icon
                                          width={24}
                                          height={24}
                                          rightGap={8}
                                          icon={
                                            <IconSVG
                                              name={'promo/sale'}
                                              width={24}
                                              height={24}
                                            />
                                          }
                                          title="Иконка Рубля"
                                        />
                                        {i.black_friday !== 1 && (
                                          <span>
                                            {i.explicit_discounts
                                              ?.seazon_discount?.value &&
                                              i.explicit_discounts
                                                ?.seazon_discount?.value}
                                            % Скидка раннего бронирования
                                          </span>
                                        )}
                                      </div>
                                    )}

                                  {sea !== 'sea' &&
                                    i.black_friday !== 1 &&
                                    !isMobile &&
                                    i.explicit_discounts?.length !== 0 &&
                                    i.explicit_discounts?.seazon_discount
                                      ?.length !== 0 &&
                                    i.explicit_discounts?.seazon_discount
                                      ?.days_before_rise && (
                                      <div className={styles.increase}>
                                        <Icon
                                          width={24}
                                          height={24}
                                          rightGap={8}
                                          icon={
                                            <IconSVG
                                              name={'promo/ruble'}
                                              width={24}
                                              height={24}
                                            />
                                          }
                                          title="Иконка Рубля"
                                        />
                                        Цена вырастет через{' '}
                                        <span className={styles.increaseDays}>
                                          {
                                            i.explicit_discounts
                                              ?.seazon_discount
                                              ?.days_before_rise
                                          }{' '}
                                          {i.explicit_discounts?.seazon_discount
                                            ?.days_before_rise &&
                                            declOfNum(
                                              i.explicit_discounts
                                                ?.seazon_discount
                                                ?.days_before_rise,
                                              ['день', 'дня', 'дней'],
                                            )}
                                        </span>
                                      </div>
                                    )}

                                  <div>
                                    {i.special_note && !isMobile && (
                                      <div className={styles.bonus}>
                                        <Icon
                                          icon={
                                            <IconSVG
                                              name={'promo/note'}
                                              width={24}
                                              height={24}
                                            />
                                          }
                                          rightGap={8}
                                          title="Спец предложение"
                                          width={24}
                                          height={24}
                                        />
                                        <Text> {i.special_note}</Text>
                                      </div>
                                    )}
                                  </div>

                                  <div>
                                    {i.special_offer !== 0 && !isMobile && (
                                      <div className={styles.bonus}>
                                        <Icon
                                          icon={
                                            <IconSVG
                                              name={'promo/present'}
                                              width={24}
                                              height={24}
                                            />
                                          }
                                          rightGap={8}
                                          title="Спец предложение"
                                          width={24}
                                          height={24}
                                        />
                                        <Text>
                                          Спецпредложение -
                                          {i.special_offer_value}%
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {authStore.isManager && (
                                <div className={styles.idsText}>ID: {i.id}</div>
                              )}
                            </div>
                          ))}
                          <div className={styles.cruisesFooter}>
                            <ShowMore
                              length={store.cruises.length}
                              amount={amount}
                              setPage={setPage}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <SkeletonRoute
                            width={!isMobile ? '1070' : '384'}
                            height={!isMobile ? '96' : '209'}
                            isMobile={isMobile}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Formik>
      </div>
    );
  },
);
